import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Activity } from 'lucide-react';
import {
  LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine,
} from 'recharts';

const fmtShort = (t) => {
  try { return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return t; }
};
const fmtLong = (t) => {
  try { return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return t; }
};

const RANGES = [
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: '12m', label: '12 months', days: 365 },
];

/**
 * HomePerformanceChart — dark "Performance over time" card. Time-based
 * X axis so amber dashed ReferenceLines can mark data-import events
 * exactly where they fall. The visible window is controlled by the
 * shared header range filter (`range` prop).
 */
export default function HomePerformanceChart({ series, imports, range = '12m' }) {
  // Measure the wrapper (via a ref callback so it attaches whenever
  // the node mounts — including after the series finishes loading) and
  // render the chart at an explicit pixel width, avoiding the Recharts
  // "width(-1)" warning.
  const wrapRef = useRef(null);
  const roRef = useRef(null);
  const [w, setW] = useState(0);
  const setWrap = useCallback((el) => {
    wrapRef.current = el;
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (el) {
      setW(Math.floor(el.getBoundingClientRect().width));
      if (typeof ResizeObserver !== 'undefined') {
        roRef.current = new ResizeObserver((entries) => {
          setW(Math.floor(entries[0]?.contentRect?.width || 0));
        });
        roRef.current.observe(el);
      }
    }
  }, []);
  const days = (RANGES.find((r) => r.id === range) || RANGES[2]).days;
  const cutoff = Date.now() - days * 86400000;

  const data = useMemo(() => {
    if (!Array.isArray(series)) return [];
    return series
      .map((p) => ({ ...p, ts: new Date(p.date).getTime() }))
      .filter((p) => !Number.isNaN(p.ts) && p.ts >= cutoff)
      .sort((a, b) => a.ts - b.ts);
  }, [series, cutoff]);

  const markers = useMemo(() => {
    if (!Array.isArray(imports) || data.length < 2) return [];
    const min = data[0].ts;
    const max = data[data.length - 1].ts;
    return imports
      .map((im) => ({ ts: new Date(im.date).getTime(), label: im.label }))
      .filter((im) => !Number.isNaN(im.ts) && im.ts >= min && im.ts <= max)
      .sort((a, b) => a.ts - b.ts)
      .slice(-8);
  }, [imports, data]);

  const hasData = data.length >= 2;
  return (
    <div className="lg:col-span-6 rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] p-5 shadow-xl" data-testid="home-performance-chart">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="h-4 w-4 text-[#44b6d2]" />
        <h3 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-200">Performance over time</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">Composite engine health (LEI · PE · ROE) across your recorded snapshots.</p>
      {hasData ? (
        <>
          <div ref={setWrap} className="h-56 w-full min-w-0" data-testid="home-performance-chart-canvas">
            {w > 0 ? (
              <RechartsLineChart width={w} height={224} data={data} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2a44" vertical={false} />
                <XAxis
                  dataKey="ts"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickFormatter={fmtShort}
                  stroke="#334155"
                  minTickGap={28}
                />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} stroke="#334155" />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #1f2a44', borderRadius: 8, color: '#e2e8f0', fontSize: 12 }}
                  labelFormatter={fmtLong}
                />
                {markers.map((m, i) => (
                  <ReferenceLine
                    key={`imp-${m.ts}-${i}`}
                    x={m.ts}
                    stroke="#f59e0b"
                    strokeDasharray="3 3"
                    strokeOpacity={0.7}
                    label={{ value: '▾', position: 'top', fill: '#f59e0b', fontSize: 10 }}
                  />
                ))}
                <Line type="monotone" dataKey="composite" name="Composite" stroke="#44b6d2" strokeWidth={3} dot={{ r: 3, fill: '#44b6d2' }} activeDot={{ r: 5 }} connectNulls />
                <Line type="monotone" dataKey="lei" name="Learning" stroke="#818cf8" strokeWidth={1.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="pe" name="Capability" stroke="#c084fc" strokeWidth={1.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="roe" name="Behavior" stroke="#2dd4bf" strokeWidth={1.5} dot={false} connectNulls />
              </RechartsLineChart>
            ) : null}
          </div>
          {markers.length > 0 && (
            <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5" data-testid="home-perf-import-legend">
              <span className="text-amber-400">▾</span> Amber markers show data imports ({markers.length} in view)
            </p>
          )}
        </>
      ) : (
        <div className="h-56 flex flex-col items-center justify-center text-center" data-testid="home-performance-chart-empty">
          <Activity className="h-8 w-8 text-slate-700 mb-2" />
          <p className="text-sm text-slate-400">Not enough history in this window…</p>
          <p className="text-xs text-slate-600 mt-1 max-w-xs">Snapshots accrue each time you open Mission Control. Try the 12-month view, or check back once more are recorded.</p>
        </div>
      )}
    </div>
  );
}
